import * as BABYLON from "babylonjs";
import { AccelerationCurve, EasingType } from "../core/accelerationCurve";
import { CoordUtils } from "../core/coordUtils";
import { GeoPoint } from "../core/geoPoint";
import { Signal } from "../core/signal";
import { EarthEngine } from "../earthEngine";
import { CameraTransition } from "./cameraTransition";

export class EarthCamera extends BABYLON.ArcRotateCamera {
    public onPan = new Signal<void> ();
    public onZoom = new Signal<void> ();
    public onMove = new Signal<void> ();

    private readonly _epsilon = 1e-6;
    private readonly _lowerSensitivity = 2000;
    private readonly _upperSensitivity = 500;
    private readonly _lowerSensitivityRadius = 1.4;
    private readonly _upperSensitivityRadius = 2;
    private _currentTransition: CameraTransition | null = null;
    private _lockCamera: BABYLON.Vector3 | null = null;
    private _previousFramePosition: BABYLON.Vector3;

    get altitude(): number {
        return this.position.length() - 1;
    }

    constructor(scene: BABYLON.Scene) {
        super("EarthCamera", 0, 0, 0, BABYLON.Vector3.Zero(), scene);

        this.inertia = 0.6;
        this.minZ = 0.2;
        this.lowerRadiusLimit = 1.25;
        this.upperRadiusLimit = 5.0;
        this.wheelPrecision = 30.0;
        this.pinchPrecision = 300.0;
        this.setPosition(new BABYLON.Vector3(0.0, 1.5, -3.0).normalize().scale(3.0));
        this.updateSensitivity();
        this._previousFramePosition = this.position.clone();

        scene.onBeforeRenderObservable.add(() => this.computeFar());
    }

    lockCamera(value: boolean) {
        if (value) {
            this._lockCamera = this.position.clone();
        } else {
            this._lockCamera = null;
        }
    }

    stopInertia() {
        this.inertialPanningX = 0;
        this.inertialPanningY = 0;
        this.inertialAlphaOffset = 0;
        this.inertialBetaOffset = 0;
        this.inertialRadiusOffset = 0;
    }

    private checkPanRotate() {
        let cameraDidZoom = false;
        let cameraDidPan = false;
        let cameraDidMove = false;
        let pos = this.position.clone();
        let prev  = this._previousFramePosition.clone();

        let posNrm = pos.length();
        let prevNrm = prev.length();

        if (Math.abs(posNrm - prevNrm) > this._epsilon) {
            //  *** Camera did zoom
            cameraDidZoom = true;
            cameraDidMove = true;
        }
        pos = pos.scale(1.0 / posNrm);
        prev = prev.scale(1.0 / prevNrm);

        if (BABYLON.Vector3.Distance(pos, prev) > this._epsilon) {
            //  *** Camera did pan
            cameraDidPan = true;
            cameraDidMove = true;
        }

        if (!this._currentTransition) {
                if (cameraDidPan) {
                    this.onPan.emit();
                }
                if (cameraDidZoom) {
                    this.onZoom.emit();
                }
                if (cameraDidMove) {
                    this.onMove.emit();
                }
        }
    }

    update() {
        super.update();
        if (this._currentTransition) {
            //  *** cleanup acceleration
            this.stopInertia();
            this._currentTransition.update(EarthEngine.instance.time);
            this.setPosition(this._currentTransition.position);
            if (!this._currentTransition.isRunning) {
                //  *** Transition over
                this._currentTransition = null;
            }
            if (this._lockCamera) {
                //  *** Update locked camera position
                this._lockCamera = this.position.clone();
            }
        } else if (this._lockCamera) {
            this.stopInertia();
            this.setPosition(this._lockCamera);
        } else {
            this.checkPanRotate();
        }

        this.updateSensitivity();

        this._previousFramePosition = this.position.clone();

    }

    updateSensitivity() {
        let radius = this.position.length();
        let ratio = (radius - this._lowerSensitivityRadius) / (this._upperSensitivityRadius - this._lowerSensitivityRadius);
        ratio = Math.min(Math.max(ratio, 0), 1);

        let sensitivity = (1 - ratio) * this._lowerSensitivity + (ratio) * this._upperSensitivity;
        this.angularSensibilityX = sensitivity;
        this.angularSensibilityY = sensitivity;
    }

    private computeFar() {
        let D = this.position.length();
        this.maxZ = (D * D - 1) / D;
    }

    focusOnPosition(coordinates: GeoPoint, cameraAltitude: number, transitionDuration: number = 0.5) {
        let radius = 1 + cameraAltitude;
        //  Clamp
        radius = Math.max(Math.min(radius, this.upperRadiusLimit), this.lowerRadiusLimit);
        let m = this.getViewMatrix(true);
        let p = CoordUtils.geoToBabylon(coordinates);
        p = p.normalize().scale(radius);
        this._currentTransition = new CameraTransition(this.position, p, transitionDuration, new AccelerationCurve(EasingType.EaseInOut));
    }

    focusHeight(cameraAltitude: number, transitionDuration: number = 0.5) {
        let radius = 1 + cameraAltitude;
        //  Clamp
        radius = Math.max(Math.min(radius, this.upperRadiusLimit), this.lowerRadiusLimit);
        let m = this.getViewMatrix(true);
        let p = this.position.clone();
        p = p.normalize().scale(radius);
        this._currentTransition = new CameraTransition(this.position, p, transitionDuration, new AccelerationCurve(EasingType.EaseInOut));
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--1!./src/earth/display/earthCamera.ts